import { h, Component } from 'preact';

import { joinClasses, accessNested, sortObjectByOrder } from 'utils/utils';
import { getValueForClientSize, getTTSizeOverride, navigateInternally } from 'utils/bolt';
import { getIsSinglePageIframed } from 'utils/bolt/client';
import { useDynamicAssetFolder } from 'utils/assets';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import HTML from 'components/core/html/html.js';
import * as webp from 'utils/bolt/webp';
import { isWebPSupported } from 'utils/isWebPSupported';
import Button from 'components/bolt/button/button';
import Countdown from 'components/bolt/countdown/countdown';
import Store from 'store/store';

import s from 'components/bolt/movieDetails/movieDetails.sss';

const loadedImages = {};

export default class MovieDetails extends Component {
	constructor(props) {
		super(props);

		this.state = {
			posterVisible: false,
			bgVideo: false,
			backgroundStyle: {},
			bgImageLoaded: false
		};

		this.hidePoster = this.hidePoster.bind(this);
		this.removeBgVideo = this.removeBgVideo.bind(this);
		this.updateBgStyle = this.updateBgStyle.bind(this);
		this.getBgImage = this.getBgImage.bind(this);
		this.getFallbackFormat = this.getFallbackFormat.bind(this);
		this.stickAndScale = this.stickAndScale.bind(this);

		Store.on('STICK_AND_SCALE', this.stickAndScale);
	}

	componentDidMount() {
		window.addEventListener('resize', this.updateBgStyle);
		this.updateBgStyle();
	}
	
	componentDidUpdate(prevProps) {
		if (prevProps.cookieHeight !== this.props.cookieHeight) {
			this.updateBgStyle();
		}
	}
	
	componentWillUnmount() {
		window.removeEventListener('resize', this.updateBgStyle);
		Store.off('STICK_AND_SCALE', this.stickAndScale);
	}

	updateBgStyle() {
		const backgroundStyle = {
			backgroundImage: this.getBgImage(),
			backgroundSize: 'cover',
			backgroundPosition: 'center'
		};

		const store = Store.get();
		const isMobile = !accessNested(store, 'client.isDesktopSize', false);
		const isSinglePageIframed = getIsSinglePageIframed();
		const sonyHeaderEnabled = !isSinglePageIframed && accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);

		if (!isMobile && this.props.cookieBannerOpen && this.props.type !== 'home') {
			let paddingTop = (this.props.cookieBannerOpen && this.props.type === 'home') ? this.props.cookieHeight : 0;

			if (sonyHeaderEnabled) {
				paddingTop += 34;
			}

			backgroundStyle.width = ((window.innerHeight - this.props.cookieHeight - paddingTop) / 900 * 609) + 'px';
		}

		this.setState({ backgroundStyle });
	}

	getFallbackFormat(value) {
		const page = { homeEnt: 'boltHomeEnt', showtimes: 'boltShowtimes', home: 'boltHome' }[this.props.type];
		const defaultFormat = value === 'tt' ? 'png' : 'jpg';
		return accessNested(this.props, `appData.pages.${page}.data.${value}.alternativeImageFormat`, defaultFormat);
	}

	getBgImage() {
		let asset = this.getBgValue('asset');
		const fallbackFormat = this.getFallbackFormat('bg');
		if (!asset) {
			return '';
		}

		// If asset is a video, bg image will be the fallback image instead
		if (/\.(mp4|webm)$/.test(asset)) {
			this.setState({ bgVideo: asset });
			asset = this.getBgValue('fallbackImg');
		}

		// Makes sure the background image is fully loaded before showing it
		if (!loadedImages[asset]) {
			loadedImages[asset] = true;
			let onLoad = () => {
				if (this.state.bgImageLoaded) {
					return;
				}
				this.setState({ bgImageLoaded: true });

				// DO SOMETHING WITH BG IMAGE LOADED
			};
			const image = new Image();
			image.onload = onLoad;
			image.onerror = onLoad;
			image.src = asset;
			// If the image takes too long to load, better to show the content
			setTimeout(onLoad, 5000);
		}

		return webp.css(asset, fallbackFormat);
	}

	getBgValue(field) {
		const backgroundData = this.props.bg;

		if (!backgroundData || !backgroundData.desktop && !backgroundData.tablet && !backgroundData.mobile) {
			return undefined;
		}

		let bgValue = backgroundData.desktop[field];

		if (backgroundData.tablet[field] || backgroundData.mobile[field]) {
			bgValue = {
				desktop: backgroundData.desktop[field],
				tablet: backgroundData.tablet[field],
				mobile: backgroundData.mobile[field]
			};
		}
		return useDynamicAssetFolder(getValueForClientSize(bgValue));
	}

	getTTPosition(tt) {
		if (!tt || !tt.titleTreatmentPosition || (!tt.titleTreatmentPosition.desktop && !tt.titleTreatmentPosition.tablet && !tt.titleTreatmentPosition.mobile)) {
			return null;
		}
		return getValueForClientSize(tt.titleTreatmentPosition);
	}

	getTT(tt) {
		if (!tt || !tt.desktop && !tt.tablet && !tt.mobile) {
			return null;
		}
		const ttSrc = getValueForClientSize(tt, null, { nullIfDisabled: true });
		return useDynamicAssetFolder(ttSrc);
	}

	hidePoster() {
		this.setState({ posterVisible: false });
	}

	removeBgVideo() {
		this.setState({ bgVideo: false });
	}

	playVideo(e) {
		tryToPlayVideo(e.target).then(this.hidePoster).catch(this.removeBgVideo);
	}

	triggerButton(path, label, sameWindow) {
		Store.emit('MOVIE_DETAILS_BUTTON_CLICK', this.props.type, label, path);
		if (path.includes('http') || path.includes('mailto')) {
			let link = document.createElement('a');
			link.href = path;
			link.target = sameWindow ? '_self' : '_blank';
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else {
			navigateInternally(path);
		}
	}

	stickAndScale(percentage) {
		if (!Store.get().client.isDesktopSize) {
			const maxTop = 16;
			const top = maxTop - (maxTop * percentage);
			this.wrapper.style.top = top + 'px';
			return this.wrapper.style.borderRadius = 20 - (20 * percentage) + 'px';
		}

		this.wrapper.style.top = '0px';
		this.wrapper.style.borderRadius = 0;
	}

	renderTT(tt = {}) {
		let { type, visible, hide } = this.props;
		let ttStyle;
		if (type === 'homeEnt') {
			type = 'showtimes';
		}
		// use hide specifically when you need it to fade out instead of disappear
		const ttSrc = this.getTT(tt);
		const ttSize = getTTSizeOverride(tt);

		if (ttSize) {
			ttStyle = {
				width: '100%',
				height: '100%',
				maxWidth: ttSize.maxWidth,
				maxHeight: ttSize.maxHeight
			};
		}
		if (!ttSrc) {
			return;
		}
		const fallbackFormat = this.getFallbackFormat('tt');
		const copy = this.props.copy;
		return (
			<webp.Picture
				style={ttStyle}
				class={joinClasses(s.tt, s[type], visible && s.visible, hide && s.hide)}
				alt={copy.accessibilityCopy[tt.alt] || tt.alt}
				src={ttSrc}
				fallbackFormat={fallbackFormat}
			/>
		);
		// return type === 'home' ? <h1>{image}</h1> : image;
	}

	renderCountdown(countdown, position) {
		let { hide, visible } = this.props;
		const { targetDateTime, annotation, timeZone = '0' } = countdown;
		const appData = this.props.appData;
		const headerHeight = Store.get().client?.headerHeight || 0;

		return (
			<Countdown
				appData={appData}
				datetime={targetDateTime}
				timeOffset={timeZone}
				annotation={annotation}
				hidden={hide}
				visible={visible}
				position={position}
				headerHeight={headerHeight}
			/>
		);
	}

	renderButtons(buttons) {
		const { appData, copy, visible, hide, buttonLayout = 'row' } = this.props;
		const fontStyles = accessNested(appData, 'styling.fontVariables') || {};

		if (buttons && Object.keys(buttons).length) {
			let store = Store.get();
			return (
				<div class={joinClasses(s.buttons, visible && s.visible, hide && s.hide, s[buttonLayout])}>
					{sortObjectByOrder(buttons).map((button, index) => {
						if (!button) {
							return;
						}
						let page = appData.pages[button.path];
						if (!button.path.startsWith('@') && !page && !button.path.match(/^https?:\/\/|mailto/)) {
							return;
						}
						if (page?.ageRestricted && store.client?.ofAge === false) {
							return;
						}
						let label = copy.baseCopy[button.label] || button.label;
						// TODO: use <a> instead of <button> if external link
						return (
							<Button
								class={joinClasses(s.button, fontStyles.homeButtons)}
								trigger={() => this.triggerButton(button.path, label, button.sameWindow)}
								type="floating"
								key={button.path + index}
								id={button.id && button.id}
							>
								<HTML
									class={s.buttonLabel}
									markdown
									content={label}
								/>
							</Button>
						);
					})}
				</div>
			);
		}
	}

	renderBgVideo() {
		const { bgVideo, posterVisible } = this.state;
		const preLoadImg = this.getBgValue('preLoadImg');
		const fallbackFormat = this.getFallbackFormat('bg');

		return (
			<div class={joinClasses({ s, bgVideo: true })} key="bgVideo">
				{posterVisible && <webp.Picture alt="" src={preLoadImg} fallbackFormat={fallbackFormat} />}
				<video
					muted
					autoPlay
					playsInline
					loop
					poster={isWebPSupported ? preLoadImg : preLoadImg.replace(/\.[^.]*$/, fallbackFormat)}
					onError={this.removeBgVideo}
					onCanPlay={this.playVideo}
					ref={$ref => this.$bgVideo = $ref}
				>
					{bgVideo.includes('.webm') && <source src={bgVideo} type="video/webm" />}
					<source src={bgVideo.replace(/\.[^.]*$/, '.mp4')} type="video/mp4" />
				</video>
			</div>
		);
	}

	renderExtraInMiddle() {
		return;
	}

	renderExtraDetail() {
		return;
	}


	render({ type, tt, bg, buttons, cookieHeight = 0, cookieBannerOpen, dualViewEnabled, isWidget }, { bgVideo, backgroundStyle }) {
		const isSinglePageIframed = getIsSinglePageIframed();

		const detailPosition = this.getTTPosition(tt);
		const store = Store.get();
		const sonyHeaderEnabled = !isSinglePageIframed && accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);
		const disableTTAnimation = !!(dualViewEnabled && accessNested(store, 'appData.pages.boltHomeEnt.data.tt') && accessNested(store, 'appData.pages.boltShowtimes.data.tt'));
		let paddingTop = (cookieBannerOpen && type === 'home') ? cookieHeight : 0;
		let paddingBottom = 0;

		if (sonyHeaderEnabled) {
			paddingTop += 34;
		}

		const evidon = accessNested(store, 'evidon.bannerOpen', false);
		const isMobile = !accessNested(store, 'client.isDesktopSize', false);
		const oneContinuousScroll = evidon && isMobile && type === 'home';

		if (oneContinuousScroll) {
			paddingTop = 0;
		}

		const showtimesShowing = accessNested(store, 'showtimes.show', false);
		const homeEntShowing = accessNested(store, 'homeEnt.show', false);
		const footerType = accessNested(store, 'appData.footer.defaultFooter.position', 'fixed');

		if (!showtimesShowing && !homeEntShowing && !oneContinuousScroll) {
			paddingBottom = !isMobile ? 40 : (footerType === 'fixed' ? 72 : 32);
			if (isWidget) {
				paddingBottom = 0;
			}
		}

		const countdownClock = accessNested(store, 'appData.options.countdownClock', false);
		const hasCountdown = countdownClock && type === 'home';
		const countdownPos = countdownClock?.position || 'standard';
		const hasMiniGallery = accessNested(store, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', false);
		const activePage = accessNested(store, 'activePage', '');
		const isHubPage = accessNested(store, `appData.pages.${activePage}.component`, '') === 'hub';

		return (
			<div
				class={joinClasses(
					s.wrapper,
					this.props.class,
					hasMiniGallery && s.miniGallery,
					isWidget && s.widget
				)}
				style={{
					paddingTop: paddingTop + 'px',
					paddingBottom: paddingBottom + 'px',
					position: oneContinuousScroll ? 'absolute' : 'fixed'
				}}
				ref={ref => this.wrapper = ref}
			>
				<div
					class={joinClasses(
						s.container,
						s[type],
						sonyHeaderEnabled && s.sonyHeader
					)}
					style={bg && backgroundStyle}
				>
					{(bgVideo && bg) && this.renderBgVideo()}
					{(hasCountdown && countdownPos === 'top') && this.renderCountdown(countdownClock, countdownPos)}
					<div class={joinClasses(s.details, s[type], s[detailPosition], disableTTAnimation && s.noAnimation)}>
						{!(isHubPage || isSinglePageIframed) && this.renderTT(tt)}
						{(hasCountdown && countdownPos === 'standard') && this.renderCountdown(countdownClock, countdownPos)}
						{this.renderExtraInMiddle()}
						{!(isHubPage || isSinglePageIframed) && this.renderButtons(buttons)}
						{this.renderExtraDetail()}
					</div>
				</div>
			</div>
		);
	}
}
